import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { adminApi } from "@/lib/apiClient";

function newId() {
  return "sm_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
}

/**
 * Admin → Contact Details
 *
 * One page owns everything customers see for contacting the business:
 *   - Homepage WhatsApp CTA number
 *   - Salesmen roster (multiple entries)
 *   - Which salesman receives Product-page Inquiry clicks (default)
 */
export default function AdminContacts() {
  const [state, setState] = useState({
    homepage_whatsapp: "",
    inquiry_default_id: "",
    salesmen: [],
  });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    adminApi
      .getSettings("contacts")
      .then((r) => {
        if (!alive) return;
        const v = r?.value || {};
        setState({
          homepage_whatsapp: v.homepage_whatsapp || "",
          inquiry_default_id: v.inquiry_default_id || "",
          salesmen: Array.isArray(v.salesmen) ? v.salesmen : [],
        });
      })
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const addSalesman = () => {
    const s = { id: newId(), name: "", whatsapp: "", email: "", label: "" };
    setState((st) => ({
      ...st,
      salesmen: [...st.salesmen, s],
      inquiry_default_id: st.inquiry_default_id || s.id,
    }));
  };

  const updateSm = (id, patch) => {
    setState((st) => ({
      ...st,
      salesmen: st.salesmen.map((s) => (s.id === id ? { ...s, ...patch } : s)),
    }));
  };

  const removeSm = (id) => {
    if (!window.confirm("Remove this contact from the roster?")) return;
    setState((st) => {
      const next = st.salesmen.filter((s) => s.id !== id);
      const default_id =
        st.inquiry_default_id === id ? (next[0]?.id || "") : st.inquiry_default_id;
      return { ...st, salesmen: next, inquiry_default_id: default_id };
    });
  };

  const setDefault = (id) => {
    setState((st) => ({ ...st, inquiry_default_id: id }));
  };

  const isValidWa = (v) => /^\+?\d[\d\s\-()]{6,}$/.test((v || "").trim());
  const canSave =
    (!state.homepage_whatsapp || isValidWa(state.homepage_whatsapp)) &&
    state.salesmen.every((s) => s.name.trim() && isValidWa(s.whatsapp));

  const save = async () => {
    setError("");
    setSaved(false);
    if (!canSave) {
      setError("Please fill every salesman's name and a valid WhatsApp number (include country code).");
      return;
    }
    setBusy(true);
    try {
      // Normalise: trim strings, strip inner whitespace from numbers we send outbound.
      const clean = {
        homepage_whatsapp: state.homepage_whatsapp.trim() || null,
        inquiry_default_id:
          state.salesmen.find((s) => s.id === state.inquiry_default_id)
            ? state.inquiry_default_id
            : state.salesmen[0]?.id || "",
        salesmen: state.salesmen.map((s) => ({
          id: s.id,
          name: s.name.trim(),
          whatsapp: s.whatsapp.trim(),
          email: (s.email || "").trim(),
          label: (s.label || "").trim(),
        })),
      };
      await adminApi.putSettings("contacts", clean);
      setSaved(true);
    } catch (err) {
      setError(err.response?.data?.error?.message || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-contacts-root">
      <header className="border-b bg-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">
              Admin › Contact details
            </div>
            <div className="text-lg font-semibold">Salesmen &amp; customer-facing numbers</div>
          </div>
          <Button asChild variant="outline"><Link to="/admin">&larr; Admin</Link></Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10 space-y-6">
        {error && (
          <Alert variant="destructive" data-testid="admin-contacts-error">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {saved && (
          <Alert data-testid="admin-contacts-saved">
            <AlertDescription>Contact details saved. Customer-facing pages update immediately.</AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Homepage contact number</CardTitle>
            <CardDescription>
              Used by the <b>WhatsApp us</b> CTA button on the homepage. Include the country code (e.g. +9715…).
              Leave blank to fall back to the salesman default.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-w-md">
              <Label htmlFor="homepage-wa">WhatsApp number</Label>
              <Input
                id="homepage-wa"
                value={state.homepage_whatsapp}
                onChange={(e) => setState((s) => ({ ...s, homepage_whatsapp: e.target.value }))}
                placeholder="+9715…"
                data-testid="admin-contacts-homepage-wa"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle className="text-base">Salesmen roster</CardTitle>
              <CardDescription>
                Add each salesman with their WhatsApp number. Mark one as <b>Default</b> —
                that number receives every product-page Inquiry click.
              </CardDescription>
            </div>
            <Button type="button" onClick={addSalesman} data-testid="admin-contacts-add">
              + Add salesman
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-sm text-muted-foreground">Loading…</div>
            ) : state.salesmen.length === 0 ? (
              <div className="text-sm text-muted-foreground border border-dashed rounded p-8 text-center">
                No salesmen yet. Add your first contact so customers can inquire on the product pages.
              </div>
            ) : (
              <div className="space-y-4" data-testid="admin-contacts-list">
                {state.salesmen.map((s, i) => (
                  <div key={s.id} className="border rounded-lg p-4" data-testid={`admin-contacts-row-${i}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {state.inquiry_default_id === s.id ? (
                          <Badge data-testid={`admin-contacts-default-badge-${i}`}>Inquiry default</Badge>
                        ) : (
                          <Button type="button" size="sm" variant="outline"
                            onClick={() => setDefault(s.id)}
                            data-testid={`admin-contacts-set-default-${i}`}>
                            Set as inquiry default
                          </Button>
                        )}
                      </div>
                      <Button type="button" size="sm" variant="ghost"
                        className="text-destructive hover:text-destructive"
                        onClick={() => removeSm(s.id)}
                        data-testid={`admin-contacts-remove-${i}`}>
                        Remove
                      </Button>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                      <div className="space-y-1.5">
                        <Label>Name</Label>
                        <Input
                          value={s.name}
                          onChange={(e) => updateSm(s.id, { name: e.target.value })}
                          placeholder="Ahmed"
                          data-testid={`admin-contacts-name-${i}`}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label>WhatsApp</Label>
                        <Input
                          value={s.whatsapp}
                          onChange={(e) => updateSm(s.id, { whatsapp: e.target.value })}
                          placeholder="+9715…"
                          data-testid={`admin-contacts-wa-${i}`}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Email <span className="text-xs text-muted-foreground">(order alerts)</span></Label>
                        <Input
                          type="email"
                          value={s.email || ""}
                          onChange={(e) => updateSm(s.id, { email: e.target.value })}
                          placeholder="salesman@fastforce12.com"
                          data-testid={`admin-contacts-email-${i}`}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Label / role (optional)</Label>
                        <Input
                          value={s.label || ""}
                          onChange={(e) => updateSm(s.id, { label: e.target.value })}
                          placeholder="Parts specialist"
                          data-testid={`admin-contacts-label-${i}`}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Separator />
        <div className="flex items-center gap-3">
          <Button onClick={save} disabled={busy} data-testid="admin-contacts-save">
            {busy ? "Saving…" : "Save contact details"}
          </Button>
          <span className="text-xs text-muted-foreground">
            Homepage &amp; product-page Inquiry buttons pick up changes on the next page load.
          </span>
        </div>
      </main>
    </div>
  );
}
